/**
 * src/utils/sanitize.js
 * ─────────────────────
 * Central HTML sanitization utility for XenBox.
 *
 * All user-generated or AI-generated HTML content MUST pass through
 * sanitizeHtml() before being rendered into the DOM.
 *
 * This prevents XSS attacks by stripping dangerous tags and attributes
 * while preserving safe formatting (bold, links, tables, etc.).
 *
 * Compliance: OWASP A03 — Injection Prevention
 *
 * Usage:
 *   import { sanitizeHtml, safeHtml } from '../utils/sanitize';
 *
 *   // For dangerouslySetInnerHTML:
 *   <div dangerouslySetInnerHTML={safeHtml(userContent)} />
 *
 *   // For direct DOM assignment:
 *   element.innerHTML = sanitizeHtml(userContent);
 */
import DOMPurify from 'dompurify';

/**
 * Core sanitization config.
 *
 * ALLOWED_TAGS: standard formatting, tables, code blocks
 * FORBID_TAGS:  script, style (inline), iframe, object, embed
 * FORBID_ATTR:  all event handlers (on*), javascript: href, data: src
 */
const BASE_CONFIG = {
  ALLOWED_TAGS: [
    // Text formatting
    'b', 'i', 'u', 'em', 'strong', 'small', 'del', 'ins', 'mark', 'sub', 'sup',
    // Structure
    'p', 'br', 'hr', 'div', 'span', 'blockquote', 'pre', 'code', 'kbd', 'samp',
    // Headings
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    // Lists
    'ul', 'ol', 'li', 'dl', 'dt', 'dd',
    // Tables
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
    // Links and images
    'a', 'img',
    // Details/summary (collapsible sections)
    'details', 'summary',
  ],
  ALLOWED_ATTR: [
    'href', 'src', 'alt', 'title', 'target', 'rel',
    'class', 'id', 'width', 'height', 'colspan', 'rowspan',
    'style',   // Needed for email HTML bodies — restricted by ALLOWED_URI_REGEXP below
    'align', 'valign', 'border', 'cellpadding', 'cellspacing',  // Legacy email attributes
  ],
  // Only allow https:// and data: (base64 images in emails) as URI schemes
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|data:image\/(?:png|jpg|jpeg|gif|webp|svg\+xml);base64,[a-zA-Z0-9+/]+=*$)/i,
  // Force target="_blank" links to also have rel="noopener noreferrer"
  ADD_ATTR: ['target'],
  FORCE_BODY: false,
};

/**
 * Strict config for AI-generated output.
 * AI content should never include links or images — strip them.
 */
const AI_CONFIG = {
  ...BASE_CONFIG,
  ALLOWED_TAGS: [
    'b', 'i', 'em', 'strong', 'code', 'pre', 'p', 'br',
    'ul', 'ol', 'li', 'blockquote', 'h1', 'h2', 'h3', 'h4',
  ],
  ALLOWED_ATTR: ['class'],
};

// Hook: force noopener on all links after sanitization
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/**
 * Sanitize arbitrary HTML content (standard config).
 * Use for: email bodies, ticket descriptions, helpdesk comments.
 *
 * @param {string} dirty - Potentially unsafe HTML string
 * @returns {string} - Safe HTML string
 */
export function sanitizeHtml(dirty) {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, BASE_CONFIG);
}

/**
 * Sanitize AI-generated text content (strict config).
 * Use for: Nexus AI chat output, AI-generated summaries.
 * Strips links and images — AI output should not contain external resources.
 *
 * @param {string} dirty - Potentially unsafe AI-generated HTML string
 * @returns {string} - Safe HTML string
 */
export function sanitizeAiHtml(dirty) {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, AI_CONFIG);
}

/**
 * Returns a React-safe dangerouslySetInnerHTML object.
 * Use for: any JSX component that renders user/AI HTML.
 *
 * @param {string} dirty - Potentially unsafe HTML string
 * @param {'standard'|'ai'} mode - Sanitization mode
 * @returns {{ __html: string }} - Safe dangerouslySetInnerHTML prop
 */
export function safeHtml(dirty, mode = 'standard') {
  const sanitized = mode === 'ai' ? sanitizeAiHtml(dirty) : sanitizeHtml(dirty);
  return { __html: sanitized };
}

export default sanitizeHtml;
